import {
  Box,
  Divider,
  HStack,
  IconButton,
  Input,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from '@chakra-ui/react';
import Editor from '@monaco-editor/react';
import { getIn, setIn } from 'formik';
import * as _ from 'lodash';
import React, { useEffect, useMemo } from 'react';

import {
  utils as commonUtils,
  finleyAssert,
  finleyAssertUnreachable,
} from '@finley-technologies/common';
import {
  CreditAgreement,
  Date,
  Deliverable,
  typeguards,
} from '@finley-technologies/finley-types';

import { WipConfig } from '../../types';
import { DeleteArrayPropertyButton } from '../delete/DeleteArrayPropertyButton';
import {
  createDeliverableArtifactPropertyShell,
  EditDeliverableArtifactModal,
  EditDeliverableModal,
} from '../edit/EditDeliverableModal';
import {
  EditRecurringDateModal,
  EditRelativeRecurringDateModal,
} from '../property/EditRecurringDateModal';
import {
  AddIconButton,
  DeleteIconButton,
  EditIconButton,
  SaveIconButton,
} from 'packages/common-frontend/components/IconButton';
import { Icon } from 'packages/common-frontend/components/icons';
import { SearchSelect } from 'packages/common-frontend/components/SearchSelect';
import {
  Select,
  formatSelectOptions,
} from 'packages/common-frontend/components/Select';
import { StickyHeaderSection } from 'packages/common-frontend/components/StickyHeaderSection';
import { getBooleanSymbol } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';

const { compact } = _;

const BASE_PROPERTY_KEY = 'deliverables';

const TABLE_HEADERS = ['Name', 'Value', 'Section', 'Type', 'Reporting Period'];
const WIP_TABLE_HEADERS = [...TABLE_HEADERS, 'Is Written in', 'Action'];

type AtLeast<T, K extends keyof T> = Partial<T> & Pick<T, K>;
type OptionalArtifactPropertyConfig = AtLeast<
  CreditAgreement.Config.DeliverableArtifactProperty.Config,
  'type' | 'relativeRecurringDateConfig' | 'configKey'
>;

const updateDeliverable = (params: {
  wipConfig: WipConfig;
  updatedDeliverable: CreditAgreement.DomainEntity.DeliverableProperty;
  idx: number;
}) => {
  const { formProps } = params.wipConfig;

  const deliverables: CreditAgreement.DomainEntity.DeliverableProperty[] = getIn(
    formProps.values,
    BASE_PROPERTY_KEY,
  );

  const newArr = [...deliverables];
  newArr[params.idx] = params.updatedDeliverable;

  formProps.setFieldValue(BASE_PROPERTY_KEY, newArr);

  const updatedWipCreditAgreement = setIn(
    formProps.values,
    BASE_PROPERTY_KEY,
    newArr,
  );

  params.wipConfig.saveCreditAgreementFullArtifactWip({
    variables: {
      creditAgreementFullArtifactWip: {
        id: updatedWipCreditAgreement.id,
        creditFacilityId: updatedWipCreditAgreement.creditFacilityId,
        creditAgreement: JSON.stringify(updatedWipCreditAgreement),
      },
    },
  });
};

const addNewDeliverable = (param: {
  wipConfig: WipConfig;
  newDeliverable: CreditAgreement.DomainEntity.DeliverableProperty;
}) => {
  const { formProps } = param.wipConfig;

  const deliverables = getIn(formProps.values, BASE_PROPERTY_KEY);

  formProps.setFieldValue(BASE_PROPERTY_KEY, [
    param.newDeliverable,
    ...deliverables,
  ]);
};

type LoanAndRepaymentTapeConfigInputsProps = {
  loanProductSlugs: string[];
  isDisabled: boolean;
  config: OptionalArtifactPropertyConfig;
  setConfig: React.Dispatch<
    React.SetStateAction<OptionalArtifactPropertyConfig>
  >;
};
const LoanAndRepaymentTapeConfigInputs = ({
  loanProductSlugs,
  isDisabled,
  config,
  setConfig,
}: LoanAndRepaymentTapeConfigInputsProps) => {
  finleyAssert(
    config.type === 'repaymentTape' || config.type === 'loanTape',
    `Deliverables: Cannot access loan tape or repayment tape config with config type ${config.type}`,
  );

  return (
    <HStack width="full">
      <Text fontSize="md" width={isDisabled ? '30%' : '23%'}>
        Loan Product Slug
      </Text>
      {isDisabled ? (
        <Text fontSize="md" width="full">
          {config.loanProductSlug}
        </Text>
      ) : (
        <Select
          value={config.loanProductSlug}
          options={formatSelectOptions(loanProductSlugs)}
          onChange={(value) => {
            if (value) {
              setConfig((current) => {
                return { ...current, loanProductSlug: value };
              });
            }
          }}
        />
      )}
    </HStack>
  );
};

type SupplementalTapeConfigInputsProps = {
  artifactSlugs: string[];
  isDisabled: boolean;
  config: OptionalArtifactPropertyConfig;
  setConfig: React.Dispatch<
    React.SetStateAction<OptionalArtifactPropertyConfig>
  >;
};
const SupplementalTapeConfigInputs = ({
  artifactSlugs,
  isDisabled,
  config,
  setConfig,
}: SupplementalTapeConfigInputsProps) => {
  finleyAssert(
    config.type === 'supplementalTape',
    `Deliverables: Cannot access supplemental tape config with config type ${config.type}`,
  );

  return (
    <HStack width="full">
      <Text fontSize="md" width={isDisabled ? '30%' : '23%'}>
        Artifact Slug
      </Text>
      {isDisabled ? (
        <Text fontSize="md" width="full">
          {config.artifactSlug}
        </Text>
      ) : (
        <Box width="full">
          <SearchSelect
            options={formatSelectOptions(artifactSlugs)}
            value={formatSelectOptions(artifactSlugs).find(
              (option) => option.value === config.artifactSlug,
            )}
            isDisabled={isDisabled}
            onChange={(value) => {
              if (value) {
                setConfig((current) => {
                  return { ...current, artifactSlug: value };
                });
              }
            }}
          />
        </Box>
      )}
    </HStack>
  );
};

type CovenantConfigInputsProps = {
  type: 'portfolioCovenant' | 'financialCovenant';
  covenantKeys: string[];
  isDisabled: boolean;
  config: OptionalArtifactPropertyConfig;
  setConfig: React.Dispatch<
    React.SetStateAction<OptionalArtifactPropertyConfig>
  >;
};
const CovenantConfigInputs = ({
  type,
  covenantKeys,
  isDisabled,
  config,
  setConfig,
}: CovenantConfigInputsProps) => {
  finleyAssert(
    config.type === type,
    `Deliverables: Cannot access portfolio covenant config with config type ${config.type}`,
  );

  const toast = useToast();
  const toastId = `${type}-toast-id`;

  if (!isDisabled && covenantKeys.length === 0 && !toast.isActive(toastId)) {
    toast({
      id: toastId,
      description: `Set up ${type} before configuring deliverables.`,
      position: 'top',
      variant: 'subtle',
      status: 'error',
      duration: 5000,
      isClosable: true,
    });
  }

  return (
    <HStack width="full">
      <Text fontSize="md" width={isDisabled ? '30%' : '23%'}>
        Covenant Key
      </Text>
      {isDisabled ? (
        <Text fontSize="md" width="full">
          {config.covenantKey}
        </Text>
      ) : (
        <Box width="full">
          <SearchSelect
            options={formatSelectOptions(covenantKeys)}
            value={formatSelectOptions(covenantKeys).find(
              (option) => option.value === config.covenantKey,
            )}
            isDisabled={isDisabled}
            onChange={(value) => {
              if (value) {
                setConfig((current) => {
                  return { ...current, covenantKey: value };
                });
              }
            }}
          />
        </Box>
      )}
    </HStack>
  );
};

type MSRConfigInputsProps = {
  reportNames: string[];
  isDisabled: boolean;
  config: OptionalArtifactPropertyConfig;
  setConfig: React.Dispatch<
    React.SetStateAction<OptionalArtifactPropertyConfig>
  >;
};
const MSRConfigInputs = ({
  reportNames,
  isDisabled,
  config,
  setConfig,
}: MSRConfigInputsProps) => {
  finleyAssert(
    config.type === 'monthlyServicerReportXlsx',
    `Deliverables: Cannot access MSR config with config type ${config.type}`,
  );

  const toast = useToast();
  const toastId = 'msr-toast-id';

  if (!isDisabled && reportNames.length === 0 && !toast.isActive(toastId)) {
    toast({
      id: toastId,
      description:
        'Set up reporting mechanics for monthlyServicerReports before configuring deliverables.',
      position: 'top',
      variant: 'subtle',
      status: 'error',
      duration: 5000,
      isClosable: true,
    });
  }

  return (
    <HStack width="full">
      <Text fontSize="md" width={isDisabled ? '30%' : '23%'}>
        Report Name
      </Text>
      {isDisabled ? (
        <Text fontSize="md" width="full">
          {config.reportName}
        </Text>
      ) : (
        <Select
          value={config.reportName}
          options={formatSelectOptions(reportNames)}
          onChange={(value) => {
            if (value) {
              setConfig((current) => {
                return { ...current, reportName: value };
              });
            }
          }}
        />
      )}
    </HStack>
  );
};

type OrgProvidedInputConfigInputsProps = {
  orgProvidedKeys: string[];
  artifactSlugs: string[];
  isDisabled: boolean;
  config: OptionalArtifactPropertyConfig;
  setConfig: React.Dispatch<
    React.SetStateAction<OptionalArtifactPropertyConfig>
  >;
};
const OrgProvidedInputConfigInputs = ({
  orgProvidedKeys,
  artifactSlugs,
  isDisabled,
  config,
  setConfig,
}: OrgProvidedInputConfigInputsProps) => {
  finleyAssert(
    config.type === 'orgProvidedInput',
    `Deliverables: Cannot access org provided input config with config type ${config.type}`,
  );

  const showArtifactSlugOption = config.artifactSlug !== undefined;

  return (
    <Stack width="full" spacing="20px">
      <HStack width="full">
        <Text fontSize="md" width={isDisabled ? '30%' : '23%'}>
          Org Provided Input Key
        </Text>
        {isDisabled ? (
          <Text fontSize="md" width="full">
            {config.key}
          </Text>
        ) : (
          <Box width="full">
            <SearchSelect
              options={formatSelectOptions(orgProvidedKeys)}
              value={formatSelectOptions(orgProvidedKeys).find(
                (option) => option.value === config.key,
              )}
              isDisabled={isDisabled}
              onChange={(value) => {
                if (value) {
                  setConfig((current) => {
                    return { ...current, key: value };
                  });
                }
              }}
            />
          </Box>
        )}
      </HStack>
      {!showArtifactSlugOption && !isDisabled && (
        <HStack>
          <Text>
            No artifact slugs are configured. If you want to support csv
            uploads, add an artifact slug.
          </Text>
          <AddIconButton
            ariaLabel="add artifact slug"
            label="Add artifact slug"
            onClick={() => {
              setConfig((current) => {
                return { ...current, artifactSlug: '' };
              });
            }}
          />
        </HStack>
      )}
      {showArtifactSlugOption && (
        <HStack width="full">
          <Text fontSize="md" width={isDisabled ? '30%' : '23%'}>
            Raw Supplemental Artifact slug
          </Text>
          {isDisabled ? (
            <Text fontSize="md" width="full">
              {config.artifactSlug}
            </Text>
          ) : (
            <>
              <Box width="full">
                <SearchSelect
                  options={formatSelectOptions(artifactSlugs)}
                  value={formatSelectOptions(artifactSlugs).find(
                    (option) => option.value === config.artifactSlug,
                  )}
                  isDisabled={isDisabled}
                  onChange={(value) => {
                    if (value) {
                      setConfig((current) => {
                        return { ...current, artifactSlug: value };
                      });
                    }
                  }}
                />
              </Box>
              <DeleteIconButton
                ariaLabel="delete artifact slug"
                label="Delete artifact slug"
                onClick={() => {
                  setConfig((current) => {
                    return { ...current, artifactSlug: undefined };
                  });
                }}
              />
            </>
          )}
        </HStack>
      )}
    </Stack>
  );
};

type FinancialStatementInputConfigInputsProps = {
  artifactSlugs: string[];
  isDisabled: boolean;
  config: OptionalArtifactPropertyConfig;
  setConfig: React.Dispatch<
    React.SetStateAction<OptionalArtifactPropertyConfig>
  >;
};
const FinancialStatementInputConfig = ({
  artifactSlugs,
  isDisabled,
  config,
  setConfig,
}: FinancialStatementInputConfigInputsProps) => {
  finleyAssert(
    config.type === 'financialStatement',
    `Deliverables: Cannot access org provided input config with config type ${config.type}`,
  );

  return (
    <Stack width="full" spacing="20px">
      <HStack width="full">
        <Text fontSize="md" width={isDisabled ? '30%' : '23%'}>
          Raw Supplemental Artifact slug
        </Text>
        {isDisabled ? (
          <Text fontSize="md" width="full">
            {config.artifactSlug}
          </Text>
        ) : (
          <Box width="full">
            <SearchSelect
              options={formatSelectOptions(artifactSlugs)}
              value={formatSelectOptions(artifactSlugs).find(
                (option) => option.value === config.artifactSlug,
              )}
              isDisabled={isDisabled}
              onChange={(value) => {
                if (value) {
                  setConfig((current) => {
                    return { ...current, artifactSlug: value };
                  });
                }
              }}
            />
          </Box>
        )}
      </HStack>
      <HStack width="full">
        <Text fontSize="md" width={isDisabled ? '30%' : '23%'}>
          Financial Data Artifact slug
        </Text>
        {isDisabled ? (
          <Text fontSize="md" width="full">
            {config.financialDataArtifactSlug}
          </Text>
        ) : (
          <Box width="full">
            <SearchSelect
              options={formatSelectOptions(artifactSlugs)}
              value={formatSelectOptions(artifactSlugs).find(
                (option) => option.value === config.financialDataArtifactSlug,
              )}
              isDisabled={isDisabled}
              onChange={(value) => {
                if (value) {
                  setConfig((current) => {
                    return { ...current, financialDataArtifactSlug: value };
                  });
                }
              }}
            />
          </Box>
        )}
      </HStack>
    </Stack>
  );
};

type MonthlyStatementInputConfigInputProps = {
  isDisabled: boolean;
  config: OptionalArtifactPropertyConfig;
  setConfig: React.Dispatch<
    React.SetStateAction<OptionalArtifactPropertyConfig>
  >;
};
const MonthlyStatementInputConfig = ({
  isDisabled,
  config,
  setConfig,
}: MonthlyStatementInputConfigInputProps) => {
  finleyAssert(
    config.type === 'monthlyStatement',
    `Deliverables: Cannot access org provided input config with config type ${config.type}`,
  );

  return (
    <Stack width="full" spacing="20px">
      <HStack width="full">
        <Text
          fontSize="md"
          width={isDisabled ? '30%' : '23%'}
          alignSelf="flex-start"
        >
          Reporting Period Start Date Formula
        </Text>
        {isDisabled && (
          <>
            <Text>{config.reportingPeriodStartDateFormula}</Text>
          </>
        )}
        {!isDisabled && (
          <>
            <Editor
              value={config.reportingPeriodStartDateFormula}
              onChange={(val) =>
                setConfig({
                  ...config,
                  reportingPeriodStartDateFormula: val,
                })
              }
              theme="vs-dark"
              width="100%"
              height="20vh"
              options={{
                fixedOverflowWidgets: true,
                minimap: { enabled: false },
              }}
            />
          </>
        )}
      </HStack>
      <HStack width="full">
        <Text fontSize="md" width={isDisabled ? '30%' : '23%'}>
          Reporting Period End Date Formula
        </Text>
        {isDisabled && (
          <>
            <Text>{config.reportingPeriodEndDateFormula}</Text>
          </>
        )}
        {!isDisabled && (
          <>
            <Editor
              value={config.reportingPeriodEndDateFormula}
              onChange={(val) =>
                setConfig({
                  ...config,
                  reportingPeriodEndDateFormula: val,
                })
              }
              theme="vs-dark"
              width="100%"
              height="20vh"
              options={{
                fixedOverflowWidgets: true,
                minimap: { enabled: false },
              }}
            />
          </>
        )}
      </HStack>
    </Stack>
  );
};

type TypeSpecificConfigInputsProps = {
  financialCovenantKeys: string[];
  portfolioCovenantKeys: string[];
  orgProvidedKeys: string[];
  loanProductSlugs: string[];
  artifactSlugs: string[];
  reportNames: string[];
  isDisabled: boolean;
  config: OptionalArtifactPropertyConfig;
  setConfig: React.Dispatch<
    React.SetStateAction<OptionalArtifactPropertyConfig>
  >;
};
const TypeSpecificConfigInputs = ({
  financialCovenantKeys,
  portfolioCovenantKeys,
  orgProvidedKeys,
  loanProductSlugs,
  artifactSlugs,
  reportNames,
  isDisabled,
  config,
  setConfig,
}: TypeSpecificConfigInputsProps) => {
  switch (config.type) {
    case 'repaymentTape':
    case 'loanTape':
      return (
        <LoanAndRepaymentTapeConfigInputs
          loanProductSlugs={loanProductSlugs}
          isDisabled={isDisabled}
          config={config}
          setConfig={setConfig}
        />
      );
    case 'financialCovenant':
      return (
        <CovenantConfigInputs
          type="financialCovenant"
          covenantKeys={financialCovenantKeys}
          isDisabled={isDisabled}
          config={config}
          setConfig={setConfig}
        />
      );
    case 'portfolioCovenant':
      return (
        <CovenantConfigInputs
          type="portfolioCovenant"
          covenantKeys={portfolioCovenantKeys}
          isDisabled={isDisabled}
          config={config}
          setConfig={setConfig}
        />
      );
    case 'orgProvidedInput':
      return (
        <OrgProvidedInputConfigInputs
          orgProvidedKeys={orgProvidedKeys}
          artifactSlugs={artifactSlugs}
          isDisabled={isDisabled}
          config={config}
          setConfig={setConfig}
        />
      );
    case 'supplementalTape':
      return (
        <SupplementalTapeConfigInputs
          artifactSlugs={artifactSlugs}
          isDisabled={isDisabled}
          config={config}
          setConfig={setConfig}
        />
      );
    case 'monthlyServicerReportXlsx':
      return (
        <MSRConfigInputs
          reportNames={reportNames}
          isDisabled={isDisabled}
          config={config}
          setConfig={setConfig}
        />
      );
    case 'financialStatement':
      return (
        <FinancialStatementInputConfig
          artifactSlugs={artifactSlugs}
          isDisabled={isDisabled}
          config={config}
          setConfig={setConfig}
        />
      );
    case 'monthlyStatement':
      return (
        <MonthlyStatementInputConfig
          isDisabled={isDisabled}
          config={config}
          setConfig={setConfig}
        />
      );
    case 'cashBalancesFeed':
      return <></>;
    default:
      return finleyAssertUnreachable(config);
  }
};

function formatConfig(config: OptionalArtifactPropertyConfig) {
  const abstractConfig = {
    type: config.type,
    configKey: config.configKey,
    relativeRecurringDateConfig: config.relativeRecurringDateConfig,
  };
  switch (config.type) {
    case 'repaymentTape':
    case 'loanTape':
      return {
        ...abstractConfig,
        loanProductSlug: config.loanProductSlug,
      };
    case 'financialCovenant':
    case 'portfolioCovenant':
      return {
        ...abstractConfig,
        covenantKey: config.covenantKey,
      };
    case 'orgProvidedInput':
      if (config.artifactSlug && config.artifactSlug.length > 0) {
        return {
          ...abstractConfig,
          key: config.key,
          artifactSlug: config.artifactSlug,
        };
      }
      return { ...abstractConfig, key: config.key };
    case 'supplementalTape':
      return { ...abstractConfig, artifactSlug: config.artifactSlug };
    case 'monthlyServicerReportXlsx':
      return {
        ...abstractConfig,
        reportName: config.reportName,
      };
    case 'financialStatement':
      return {
        ...abstractConfig,
        artifactSlug: config.artifactSlug,
        financialDataArtifactSlug: config.financialDataArtifactSlug,
      };
    case 'monthlyStatement':
      return {
        ...abstractConfig,
        reportingPeriodStartDateFormula: config.reportingPeriodStartDateFormula,
        reportingPeriodEndDateFormula: config.reportingPeriodEndDateFormula,
      };
    default:
      return abstractConfig;
  }
}

type ArtifactPropertyConfigProps = {
  financialCovenantKeys: string[];
  portfolioCovenantKeys: string[];
  orgProvidedKeys: string[];
  loanProductSlugs: string[];
  artifactSlugs: string[];
  reportNames: string[];
  config: CreditAgreement.Config.DeliverableArtifactProperty.Config;
  isEditable: boolean;
  idx: number;
  setConfig: React.Dispatch<
    React.SetStateAction<
      CreditAgreement.DomainEntity.DeliverableArtifactProperty[]
    >
  >;
};
const ArtifactPropertyConfig = ({
  financialCovenantKeys,
  portfolioCovenantKeys,
  orgProvidedKeys,
  loanProductSlugs,
  artifactSlugs,
  reportNames,
  config,
  isEditable,
  idx,
  setConfig,
}: ArtifactPropertyConfigProps) => {
  const toast = useToast();
  const toastId = 'toast-id';

  const [
    isRelativeDateModalOpen,
    setIsRelativeDateModalOpen,
  ] = React.useState<boolean>(false);

  // config state
  const [
    localConfig,
    setLocalConfig,
  ] = React.useState<OptionalArtifactPropertyConfig>(config);

  useEffect(() => {
    const formattedConfig = formatConfig(localConfig);

    if (
      typeguards.creditAgreement.isDeliverableArtifactPropertyConfig(
        formattedConfig,
      )
    ) {
      setConfig((current) => {
        const newArr = [...current];
        const currentProperty = newArr[idx];
        newArr[idx] = { ...currentProperty, _config_: formattedConfig };
        return newArr;
      });
    } else if (!toast.isActive(toastId)) {
      toast({
        id: toastId,
        description: 'Validation failed for Artifact Config',
        position: 'top',
        variant: 'subtle',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [localConfig, idx, setConfig, toast]);

  return (
    <Stack width="100%" alignItems="flex-start" spacing="20px">
      <Text fontSize="lg" fontWeight="bold">
        Config
      </Text>
      <HStack width="full">
        <Text fontSize="md" width={!isEditable ? '30%' : '23%'}>
          Artifact Type
        </Text>
        {!isEditable ? (
          <Text fontSize="md" width="full">
            {localConfig.type}
          </Text>
        ) : (
          <Select
            value={localConfig.type}
            options={formatSelectOptions(
              Deliverable.DeliverableTypes,
              'sentence',
            )}
            onChange={(value) => {
              if (value) {
                setLocalConfig((current) => {
                  return { ...current, type: value };
                });
              }
            }}
          />
        )}
      </HStack>
      <HStack width="full">
        <Text fontSize="md" width={!isEditable ? '30%' : '23%'}>
          Config Key
        </Text>
        {!isEditable ? (
          <Text fontSize="md" width="full">
            {localConfig.configKey}
          </Text>
        ) : (
          <Input
            value={localConfig.configKey}
            onChange={(e) => {
              setLocalConfig((current) => {
                return { ...current, configKey: _.camelCase(e.target.value) };
              });
            }}
          />
        )}
      </HStack>
      <TypeSpecificConfigInputs
        financialCovenantKeys={financialCovenantKeys}
        portfolioCovenantKeys={portfolioCovenantKeys}
        orgProvidedKeys={orgProvidedKeys}
        loanProductSlugs={loanProductSlugs}
        artifactSlugs={artifactSlugs}
        reportNames={reportNames}
        isDisabled={!isEditable}
        config={localConfig}
        setConfig={setLocalConfig}
      />
      <HStack width="full">
        <Text fontSize="md">Recurring Due Date Config</Text>
        {isEditable && (
          <EditIconButton
            ariaLabel="edit"
            label="Edit"
            onClick={() => {
              setIsRelativeDateModalOpen(true);
            }}
          />
        )}
      </HStack>
      <Editor
        defaultLanguage="json"
        value={JSON.stringify(localConfig.relativeRecurringDateConfig, null, 4)}
        options={{ readOnly: true }}
        theme="vs-dark"
        height="15vh"
        width="100%"
      />
      <EditRelativeRecurringDateModal
        relativeDateConfig={localConfig.relativeRecurringDateConfig}
        isOpen={isRelativeDateModalOpen}
        displayName="Relative Recurring Date"
        onCancel={() => setIsRelativeDateModalOpen(false)}
        onSave={(relativeConfig: Date.RecurringDate.RelativeConfig) => {
          setLocalConfig((current) => {
            return { ...current, relativeRecurringDateConfig: relativeConfig };
          });
          setIsRelativeDateModalOpen(false);
        }}
      />
    </Stack>
  );
};

type ArtifactPropertyRowProps = {
  artifactProperty: CreditAgreement.DomainEntity.DeliverableArtifactProperty;
  wipConfig?: WipConfig;
  propertyKey: string;
  columnWidth: string;
  artifactsLength: number;
  headersLength: number;
  idx: number;
  onSubmit: React.Dispatch<
    React.SetStateAction<
      CreditAgreement.DomainEntity.DeliverableArtifactProperty[]
    >
  >;
  onDelete: () => void;
  fullPropertyKey: string;
};
const ArtifactPropertyRow = ({
  artifactProperty,
  wipConfig,
  propertyKey,
  columnWidth,
  artifactsLength,
  headersLength,
  idx,
  onSubmit,
  onDelete,
  fullPropertyKey,
}: ArtifactPropertyRowProps) => {
  const {
    name,
    value,
    section,
    reportingPeriod,
    _isWrittenIn_,
    _config_,
  } = artifactProperty;

  const [isEditModalOpen, setIsEditModalOpen] = React.useState<boolean>(false);
  const [isConfigOpen, setIsConfigOpen] = React.useState<boolean>(false);

  useEffect(() => {
    setIsEditModalOpen(false);
    setIsConfigOpen(false);
  }, [artifactsLength]);

  const loanProductSlugs = wipConfig
    ? Object.values(wipConfig.loanProductsMap).map((lp) => lp.slug)
    : [];

  return (
    <>
      <Tr borderTop="1px" borderColor="gray.700">
        <Td w={columnWidth}>{commonUtils.toSentenceCase(name)}</Td>
        <Td w={columnWidth}>{value}</Td>
        <Td w={columnWidth}>{section || '-'}</Td>
        <Td w={columnWidth}>{commonUtils.toSentenceCase(_config_.type)}</Td>
        <Td w={columnWidth}>
          {commonUtils.toSentenceCase(reportingPeriod.period)}
        </Td>

        {wipConfig?.isMutateAllowed(fullPropertyKey) && (
          // TODO _isWrittenIn_ should just be rendered conditional based upon
          // if it exists or not, not by the presence of WIP, see other examples patterns
          // in CA to fix
          <>
            <Td w={columnWidth}>{getBooleanSymbol(_isWrittenIn_)}</Td>
            <Td w={columnWidth}>
              <EditIconButton
                ariaLabel="edit"
                label="Edit"
                onClick={() => {
                  setIsEditModalOpen(true);
                }}
              />
              <DeleteIconButton
                ariaLabel="delete"
                label="Delete"
                onClick={() => {
                  onDelete();
                }}
              />
            </Td>
          </>
        )}

        <Td width={columnWidth}>
          {isConfigOpen ? (
            <IconButton
              aria-label="close config"
              icon={<Icon icon="chevron-up" />}
              onClick={() => setIsConfigOpen(false)}
              variant="ghost"
            />
          ) : (
            <IconButton
              aria-label="display config"
              icon={<Icon icon="chevron-down" />}
              onClick={() => setIsConfigOpen(true)}
              variant="ghost"
            />
          )}
        </Td>
      </Tr>

      {isConfigOpen && (
        <Tr borderTop="1px" borderColor="gray.700">
          <Td colSpan={headersLength}>
            <ArtifactPropertyConfig
              orgProvidedKeys={
                wipConfig?.isMutateAllowed(fullPropertyKey)
                  ? wipConfig.orgProvidedInputKeys
                  : []
              }
              portfolioCovenantKeys={
                wipConfig?.isMutateAllowed(fullPropertyKey)
                  ? wipConfig.portfolioCovenantKeys
                  : []
              }
              financialCovenantKeys={
                wipConfig?.isMutateAllowed(fullPropertyKey)
                  ? wipConfig.financialCovenantKeys
                  : []
              }
              reportNames={
                wipConfig?.isMutateAllowed(fullPropertyKey)
                  ? wipConfig.reportNamesMap.monthlyServicerReport
                  : []
              }
              artifactSlugs={
                wipConfig?.isMutateAllowed(fullPropertyKey)
                  ? wipConfig.artifactSlugs
                  : []
              }
              loanProductSlugs={loanProductSlugs}
              config={artifactProperty._config_}
              isEditable={!!wipConfig?.isMutateAllowed(fullPropertyKey)}
              idx={idx}
              setConfig={onSubmit}
            />
          </Td>
        </Tr>
      )}

      {wipConfig?.isMutateAllowed(fullPropertyKey) && (
        <Tr>
          <Td colSpan={headersLength}>
            <EditDeliverableArtifactModal
              isOpen={isEditModalOpen}
              setIsOpen={setIsEditModalOpen}
              basePropertyKey={propertyKey}
              wipConfig={wipConfig}
              deliverableArtifact={artifactProperty}
              onSubmit={(input) => {
                onSubmit((current) => {
                  const newArr = [...current];
                  newArr[idx] = input;
                  return newArr;
                });
              }}
            />
          </Td>
        </Tr>
      )}
    </>
  );
};

type ArtifactPropertyTableProps = {
  artifacts: CreditAgreement.DomainEntity.DeliverableArtifactProperty[];
  isFinalArtifact: boolean;
  wipConfig?: WipConfig;
  setArtifacts: React.Dispatch<
    React.SetStateAction<
      CreditAgreement.DomainEntity.DeliverableArtifactProperty[]
    >
  >;
  fullPropertyKey: string;
};
const ArtifactPropertyTable = ({
  artifacts,
  isFinalArtifact,
  wipConfig,
  setArtifacts,
  fullPropertyKey,
}: ArtifactPropertyTableProps) => {
  const { tableHeaders, columnWidth } = useMemo<{
    tableHeaders: string[];
    columnWidth: string;
  }>(() => {
    const headers: string[] = wipConfig ? WIP_TABLE_HEADERS : TABLE_HEADERS;
    const width: string = `${Math.floor(95 / (headers.length + 1))}%`;
    return { tableHeaders: headers, columnWidth: width };
  }, [wipConfig]);
  return (
    <Stack spacing="20px">
      <Divider pt={4} />
      <HStack pt={4}>
        <Text fontSize="lg" fontWeight="bold">
          {isFinalArtifact ? 'Final Artifact(s)' : 'Dependencies'}
        </Text>
        {wipConfig?.isMutateAllowed(fullPropertyKey) && (
          <AddIconButton
            ariaLabel="add a new artifact"
            label="Add an artifact"
            onClick={() =>
              setArtifacts((current) => [
                ...current,
                createDeliverableArtifactPropertyShell(isFinalArtifact),
              ])
            }
          />
        )}
      </HStack>
      {isFinalArtifact && (
        <Text fontSize="md" fontWeight="bold">
          We will generate the final artifacts in order. This means the previous
          artifact must succeed before we generate the subsequent one.
        </Text>
      )}
      {artifacts.length === 0 ? (
        <Text>{`There are currently no artifacts set up. Add a ${
          isFinalArtifact ? 'final' : 'dependency'
        } artifact here.`}</Text>
      ) : (
        <Box>
          <Table
            style={{ tableLayout: 'fixed' }}
            borderWidth="1px"
            id="deliverables-table"
          >
            <Thead>
              <Tr>
                {tableHeaders.map((header) => (
                  <Th width={columnWidth} key={header}>
                    {header}
                  </Th>
                ))}
                {wipConfig && <Th width="5%" />}
              </Tr>
            </Thead>
            <Tbody>
              {artifacts.map((property, idx) => {
                const key = `${property.name} [${[idx]}]`;
                return (
                  <ArtifactPropertyRow
                    key={key}
                    artifactProperty={property}
                    wipConfig={wipConfig}
                    propertyKey={key}
                    columnWidth={columnWidth}
                    headersLength={tableHeaders.length}
                    artifactsLength={artifacts.length}
                    idx={idx}
                    fullPropertyKey={fullPropertyKey}
                    onSubmit={setArtifacts}
                    onDelete={() => {
                      if (wipConfig) {
                        setArtifacts((current) => {
                          const newArr = [...current];
                          newArr.splice(idx, 1);
                          return newArr;
                        });
                      }
                    }}
                  />
                );
              })}
            </Tbody>
          </Table>
        </Box>
      )}
    </Stack>
  );
};

type DeliverablePropertyConfigProps = {
  isEditable: boolean;
  deliverablePropertyConfig: CreditAgreement.Config.DeliverableProperty.Config;
  setConfig: React.Dispatch<
    React.SetStateAction<CreditAgreement.Config.DeliverableProperty.Config>
  >;
};
const DeliverablePropertyConfig = ({
  isEditable,
  deliverablePropertyConfig,
  setConfig,
}: DeliverablePropertyConfigProps) => {
  const [
    isRecurringDueDateModalOpen,
    setIsRecurringDueDateModalOpen,
  ] = React.useState<boolean>(false);

  const [configKey, setConfigKey] = React.useState<string>(
    deliverablePropertyConfig.configKey,
  );

  const [daysOffset, setDaysOffset] = React.useState<number>(
    deliverablePropertyConfig.daysOffset,
  );

  const [
    recurringDueDateConfig,
    setRecurringDueDateConfig,
  ] = React.useState<Date.RecurringDate.Config>(
    deliverablePropertyConfig.recurringDueDateConfig,
  );

  useEffect(() => {
    setConfig({ configKey, daysOffset, recurringDueDateConfig });
  }, [configKey, daysOffset, recurringDueDateConfig, setConfig]);

  return (
    <Stack pt={4} width="100%" alignItems="flex-start" spacing="20px">
      <Text fontSize="lg" fontWeight="bold">
        Config
      </Text>
      <HStack width="full">
        <Text fontSize="md" width={!isEditable ? '30%' : '23%'}>
          Config Key
        </Text>
        {!isEditable ? (
          <Text fontSize="md" width="full">
            {configKey}
          </Text>
        ) : (
          <Input
            value={configKey}
            onChange={(e) => setConfigKey(_.camelCase(e.target.value))}
          />
        )}
      </HStack>
      <HStack width="full">
        <Text fontSize="md" width={!isEditable ? '30%' : '23%'}>
          Days Offset
        </Text>
        {!isEditable ? (
          <Text fontSize="md" width="full">
            {daysOffset}
          </Text>
        ) : (
          <NumberInput
            width="full"
            value={daysOffset}
            min={1}
            onChange={(value) => {
              setDaysOffset(Number(value));
            }}
          >
            <NumberInputField />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
        )}
      </HStack>
      <HStack width="full">
        <Text fontSize="md">Recurring Due Date Config</Text>
        {isEditable && (
          <EditIconButton
            ariaLabel="edit"
            label="Edit"
            onClick={() => {
              setIsRecurringDueDateModalOpen(true);
            }}
          />
        )}
      </HStack>
      <Editor
        defaultLanguage="json"
        value={JSON.stringify(recurringDueDateConfig, null, 4)}
        options={{ readOnly: true }}
        theme="vs-dark"
        height="15vh"
        width="100%"
      />
      <EditRecurringDateModal
        recurringDateConfig={recurringDueDateConfig}
        isOpen={isRecurringDueDateModalOpen}
        onCancel={() => setIsRecurringDueDateModalOpen(false)}
        onSave={(config: Date.RecurringDate.Config) => {
          setRecurringDueDateConfig(config);
          setIsRecurringDueDateModalOpen(false);
        }}
        displayName="Recurring Date"
      />
    </Stack>
  );
};

type DeliverableContainerProps = {
  deliverableProperty: CreditAgreement.DomainEntity.DeliverableProperty;
  wipConfig?: WipConfig;
  fullPropertyKey: string;
  columnWidth: string;
  headersLength: number;
  propertyIdx: number;
  basePropertyKey: string;
};
const DeliverableContainer = ({
  deliverableProperty,
  wipConfig,
  fullPropertyKey,
  columnWidth,
  headersLength,
  propertyIdx,
  basePropertyKey,
}: DeliverableContainerProps) => {
  const {
    name,
    value,
    section,
    reportingPeriod,
    _isWrittenIn_,
    containerType,
  } = deliverableProperty;

  const [isEditOpen, setIsEditOpen] = React.useState<boolean>(false);
  const [isConfigOpen, setIsConfigOpen] = React.useState<boolean>(false);

  const [
    deliverableConfig,
    setDeliverableConfig,
  ] = React.useState<CreditAgreement.Config.DeliverableProperty.Config>(
    deliverableProperty._config_,
  );

  const [finalArtifacts, setFinalArtifacts] = React.useState<
    CreditAgreement.DomainEntity.DeliverableArtifactProperty[]
  >(deliverableProperty.finalArtifacts);

  const [dependencies, setDependencies] = React.useState<
    CreditAgreement.DomainEntity.DeliverableArtifactProperty[]
  >(deliverableProperty.dependencies);

  useEffect(() => {
    setDeliverableConfig(deliverableProperty._config_);
    setFinalArtifacts(deliverableProperty.finalArtifacts);
    setDependencies(deliverableProperty.dependencies);
  }, [
    deliverableProperty._config_,
    deliverableProperty.dependencies,
    deliverableProperty.finalArtifacts,
  ]);

  return (
    <>
      <Tr borderTop="1px" borderColor="gray.700">
        <Td w={columnWidth}>{commonUtils.toSentenceCase(name)}</Td>
        <Td w={columnWidth}>{value}</Td>
        <Td w={columnWidth}>{section || '-'}</Td>
        <Td w={columnWidth}>{commonUtils.toSentenceCase(containerType)}</Td>
        <Td w={columnWidth}>
          {commonUtils.toSentenceCase(reportingPeriod.period)}
        </Td>

        {wipConfig?.isMutateAllowed(fullPropertyKey) && (
          <>
            <Td w={columnWidth}>{getBooleanSymbol(_isWrittenIn_)}</Td>
            <Td w={columnWidth}>
              <EditIconButton
                ariaLabel="edit"
                label="Edit"
                onClick={() => {
                  setIsEditOpen(true);
                }}
              />
              <DeleteArrayPropertyButton
                wipConfig={wipConfig}
                basePropertyKey={basePropertyKey}
                idx={propertyIdx}
              />
            </Td>
          </>
        )}

        <Td width={columnWidth}>
          {isConfigOpen ? (
            <IconButton
              aria-label="close config"
              icon={<Icon icon="chevron-up" />}
              onClick={() => setIsConfigOpen(false)}
              variant="ghost"
            />
          ) : (
            <IconButton
              aria-label="display config"
              icon={<Icon icon="chevron-down" />}
              onClick={() => setIsConfigOpen(true)}
              variant="ghost"
            />
          )}
        </Td>
      </Tr>

      {isConfigOpen && (
        <Tr borderTop="1px" borderColor="gray.700">
          <Td colSpan={headersLength}>
            <HStack my={4}>
              <Text fontSize="xl" fontWeight="bold">
                Additional Sections
              </Text>
              {wipConfig?.isMutateAllowed(fullPropertyKey) && (
                <SaveIconButton
                  ariaLabel="save config, final artifacts, and dependencies"
                  label="Save Config, Final Artifacts, and Dependencies"
                  onClick={() => {
                    const updatedDeliverable = {
                      ...deliverableProperty,
                      finalArtifacts,
                      dependencies,
                      _config_: deliverableConfig,
                    };
                    updateDeliverable({
                      wipConfig,
                      updatedDeliverable,
                      idx: propertyIdx,
                    });
                  }}
                />
              )}
            </HStack>
            <Divider />
            <DeliverablePropertyConfig
              isEditable={!!wipConfig?.isMutateAllowed(fullPropertyKey)}
              deliverablePropertyConfig={deliverableProperty._config_}
              setConfig={setDeliverableConfig}
            />
            <ArtifactPropertyTable
              artifacts={dependencies}
              wipConfig={wipConfig}
              isFinalArtifact={false}
              setArtifacts={setDependencies}
              fullPropertyKey={fullPropertyKey}
            />
            <ArtifactPropertyTable
              artifacts={finalArtifacts}
              wipConfig={wipConfig}
              isFinalArtifact
              setArtifacts={setFinalArtifacts}
              fullPropertyKey={fullPropertyKey}
            />
          </Td>
        </Tr>
      )}

      {!!wipConfig && (
        <EditDeliverableModal
          isOpen={isEditOpen}
          setIsOpen={setIsEditOpen}
          basePropertyKey={fullPropertyKey}
          wipConfig={wipConfig}
          deliverable={deliverableProperty}
          onSubmit={(
            updatedDeliverable: CreditAgreement.DomainEntity.DeliverableProperty,
          ) => {
            updateDeliverable({
              wipConfig,
              updatedDeliverable,
              idx: propertyIdx,
            });
          }}
        />
      )}
    </>
  );
};

type DeliverableTableProps = {
  deliverables: CreditAgreement.DomainEntity.DeliverableProperty[];
  wipConfig?: WipConfig;
  basePropertyKey: string;
};
const DeliverableTable = ({
  deliverables,
  wipConfig,
  basePropertyKey,
}: DeliverableTableProps) => {
  const { tableHeaders, columnWidth } = useMemo<{
    tableHeaders: string[];
    columnWidth: string;
  }>(() => {
    const headers: string[] = wipConfig ? WIP_TABLE_HEADERS : TABLE_HEADERS;
    const width: string = `${Math.floor(95 / (headers.length + 1))}%`;
    return { tableHeaders: headers, columnWidth: width };
  }, [wipConfig]);

  if (!deliverables.length) {
    return <Text>There are currently no deliverables set up.</Text>;
  }

  return (
    <Box>
      <Table
        style={{ tableLayout: 'fixed' }}
        borderWidth="1px"
        id="deliverables-table"
      >
        <Thead>
          <Tr>
            {tableHeaders.map((header) => (
              <Th width={columnWidth} key={header}>
                {header}
              </Th>
            ))}
            {wipConfig && <Th width="5%" />}
          </Tr>
        </Thead>
        <Tbody>
          {deliverables.map((property, idx) => {
            const fullPropertyKey = `${basePropertyKey}[${[idx]}]`;
            return (
              <DeliverableContainer
                key={fullPropertyKey}
                deliverableProperty={property}
                wipConfig={wipConfig}
                fullPropertyKey={fullPropertyKey}
                columnWidth={columnWidth}
                headersLength={tableHeaders.length}
                propertyIdx={idx}
                basePropertyKey={basePropertyKey}
              />
            );
          })}
        </Tbody>
      </Table>
    </Box>
  );
};

type DeliverablesProps = {
  deliverables: CreditAgreement.DomainEntity.DeliverableProperty[];
  wipConfig?: WipConfig;
};
export const Deliverables = ({
  deliverables,
  wipConfig,
}: DeliverablesProps) => {
  const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);

  if (
    !wipConfig?.isMutateAllowed(BASE_PROPERTY_KEY) &&
    !compact(deliverables).length
  ) {
    return null;
  }

  return (
    <>
      {wipConfig?.isMutateAllowed(BASE_PROPERTY_KEY) ? (
        <Box mt="40px">
          <StickyHeaderSection
            labelText="[V4] Deliverables"
            labelRightComponent={
              wipConfig.isMutateAllowed(BASE_PROPERTY_KEY) ? (
                <>
                  <AddIconButton
                    ariaLabel="add a new deliverable"
                    label="Add a new deliverable"
                    onClick={() => setIsModalOpen(true)}
                  />
                  <EditDeliverableModal
                    isOpen={isModalOpen}
                    setIsOpen={setIsModalOpen}
                    basePropertyKey={BASE_PROPERTY_KEY}
                    wipConfig={wipConfig}
                    onSubmit={(
                      newDeliverable: CreditAgreement.DomainEntity.DeliverableProperty,
                    ) => {
                      addNewDeliverable({ wipConfig, newDeliverable });
                    }}
                  />
                </>
              ) : null
            }
          >
            <DeliverableTable
              deliverables={deliverables}
              basePropertyKey={BASE_PROPERTY_KEY}
              wipConfig={wipConfig}
            />
          </StickyHeaderSection>
        </Box>
      ) : (
        <StickyHeaderSection labelText="[V4] Deliverables">
          <DeliverableTable
            deliverables={deliverables}
            basePropertyKey={BASE_PROPERTY_KEY}
          />
        </StickyHeaderSection>
      )}
    </>
  );
};
